/**
 * AutomationRuleCard Component
 *
 * Displays a single automation rule with its status, schedule, stats,
 * and action buttons for edit, pause/resume, and delete operations.
 */

import { memo, useState, useCallback } from 'react';
import type { IAutomationRuleCardProps, IAutomationSchedule } from '@/@types/automation';
import { AutomationStatusBadge } from './AutomationStatusBadge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  Camera,
  FileText,
  Video,
  Calendar,
  Users,
  Clock,
  Pencil,
  Pause,
  Play,
  Trash2,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * Day names for schedule display
 */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Format schedule for display
 */
function formatScheduleDisplay(schedule: IAutomationSchedule): string {
  const enabledDays = schedule.daysOfWeek
    .sort((a, b) => a - b)
    .map(dayIndex => DAY_NAMES[dayIndex])
    .join(', ');

  // Convert 24h time to 12h format with AM/PM
  const [hours, minutes] = schedule.timeOfDay.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const timeDisplay = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;

  return `${enabledDays} at ${timeDisplay}`;
}

/**
 * Get content type icon component
 */
function getContentTypeIcon(contentType: 'photo' | 'post' | 'reel'): React.ReactNode {
  switch (contentType) {
    case 'photo':
      return <Camera className="h-4 w-4" />;
    case 'post':
      return <FileText className="h-4 w-4" />;
    case 'reel':
      return <Video className="h-4 w-4" />;
    default:
      return null;
  }
}

/**
 * Get content type label for display
 */
function getContentTypeLabel(contentType: 'photo' | 'post' | 'reel'): string {
  switch (contentType) {
    case 'photo':
      return 'Photo';
    case 'post':
      return 'Text Post';
    case 'reel':
      return 'Video Reel';
    default:
      return contentType;
  }
}

export const AutomationRuleCard = memo(function AutomationRuleCard({
  rule,
  lastExecution,
  onEdit,
  onToggleStatus,
  onDelete,
  onTestRun,
  className,
}: IAutomationRuleCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const handleEdit = useCallback(() => {
    onEdit(rule._id);
  }, [onEdit, rule._id]);

  const handleToggleStatus = useCallback(async () => {
    setIsToggling(true);
    try {
      onToggleStatus(rule._id);
    } finally {
      setIsToggling(false);
    }
  }, [onToggleStatus, rule._id]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      onDelete(rule._id);
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, rule._id]);

  const handleTestRun = useCallback(() => {
    if (!onTestRun) return;
    setIsTesting(true);
    onTestRun(rule._id);
    // Reset isTesting after a short delay (modal will show progress)
    setTimeout(() => setIsTesting(false), 1000);
  }, [onTestRun, rule._id]);

  // Determine last run display
  const lastRunDisplay = lastExecution
    ? formatDistanceToNow(lastExecution.scheduledFor, { addSuffix: true })
    : 'Never';

  const isPaused = rule.status === 'paused';
  const isDraft = rule.status === 'draft';

  return (
    <Card className={cn('flex flex-col', className)}>
      {/* Header */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              {getContentTypeIcon(rule.contentType)}
            </div>
            <div>
              <CardTitle className="text-base">{rule.name}</CardTitle>
              <CardDescription className="text-xs">
                {getContentTypeLabel(rule.contentType)}
              </CardDescription>
            </div>
          </div>
          <AutomationStatusBadge status={rule.status} />
        </div>
      </CardHeader>

      {/* Body */}
      <CardContent className="flex-1 space-y-3 pb-3">
        {/* Schedule */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4 shrink-0" />
          <span className="truncate">{formatScheduleDisplay(rule.schedule)}</span>
        </div>

        {/* Target accounts count */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4 shrink-0" />
          <span>
            {rule.targetAccountIds.length} target account
            {rule.targetAccountIds.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Last run */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4 shrink-0" />
          <span>Last run: {lastRunDisplay}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BarChart3 className="h-4 w-4 shrink-0" />
          <span>
            {rule.stats.totalGenerated} generated, {rule.stats.totalPublished} published
          </span>
        </div>
      </CardContent>

      {/* Footer with action buttons */}
      <CardFooter className="gap-2 border-t pt-3">
        <Button variant="outline" size="sm" onClick={handleEdit} className="flex-1">
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleStatus}
          disabled={isToggling || isDraft}
          className="flex-1"
        >
          {isPaused ? (
            <>
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Resume
            </>
          ) : (
            <>
              <Pause className="mr-1.5 h-3.5 w-3.5" />
              Pause
            </>
          )}
        </Button>

        {/* Test Run Button */}
        {onTestRun && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestRun}
            disabled={isTesting}
            title="Test Run"
          >
            {isTesting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </Button>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              disabled={isDeleting}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Automation Rule</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{rule.name}&quot;? This action cannot be
                undone and will also delete all associated queue items.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>
    </Card>
  );
});
